/* eslint-disable react/display-name */
import {
    toDurationStringFromSeconds,
    toSecondsFromDurationString,
} from 'cleveron-web-components/utils';
import ipRegex from 'ip-regex';
import React from 'react';
import { FormattedMessage } from 'react-intl';

export const required = value =>
    value || typeof value === 'number' ? undefined : (
        <FormattedMessage defaultMessage="Required" id="validation.required" />
    );

export const notBlank = value =>
    required(value) ||
    (!value.trim || value.trim() ? undefined : (
        <FormattedMessage defaultMessage="Required" id="validation.notBlank" />
    ));

export const isKey = value =>
    !value || /^[A-Z0-9_]+$/.test(value) ? undefined : (
        <FormattedMessage defaultMessage="Only A-Z 0-9 _ allowed" id="validation.isKey" />
    );

export const minLength = len => value =>
    // eslint-disable-next-line react/destructuring-assignment
    !value || !value.length || value.length >= len ? undefined : (
        <FormattedMessage
            defaultMessage="Min length {length}"
            id="validation.minLength"
            values={{ length: len }}
        />
    );

export const maxLength = len => value =>
    // eslint-disable-next-line react/destructuring-assignment
    !value || !value.length || value.length <= len ? undefined : (
        <FormattedMessage
            defaultMessage="Max length {length}"
            id="validation.maxLength"
            values={{ length: len }}
        />
    );

export const matchWith = fieldName => (value, values) =>
    // eslint-disable-next-line react/destructuring-assignment
    !value || value === values[fieldName] ? undefined : (
        <FormattedMessage
            defaultMessage="Should match with {fieldName}"
            id="validation.matchWith"
            values={{ fieldName }}
        />
    );

export const atLeastOneRequired = value =>
    !value || (value && value.length === 0) ? (
        <FormattedMessage
            defaultMessage="At least one is required"
            id="validation.atLeastOneRequired"
        />
    ) : undefined;

export const isInteger = value =>
    !value || Number.isInteger(value) ? undefined : (
        <FormattedMessage defaultMessage="Should be Integer" id="validation.isInteger" />
    );

export const isAtleast = num => value =>
    !value || value >= num ? undefined : (
        <FormattedMessage
            defaultMessage="Should be at least {num}"
            id="validation.isAtleast"
            values={{ num }}
        />
    );

export const isLessThan = num => value =>
    !value || value < num ? undefined : (
        <FormattedMessage
            defaultMessage="Should be less than {num}"
            id="validation.isLessThan"
            values={{ num }}
        />
    );

export const isGreaterThan = num => value =>
    !value || value > num ? undefined : (
        <FormattedMessage
            defaultMessage="Should be greater than {num}"
            id="validation.isGreaterThan"
            values={{ num }}
        />
    );

export const isHostname = value =>
    (!value?.match(/^[a-z0-9-_.]{3,64}$/) && (
        <FormattedMessage defaultMessage="Enter a valid hostname" id="validation.isHostname" />
    )) ||
    undefined;

export const isValidRegexExpression = value => {
    try {
        RegExp(value);
    } catch {
        return (
            <FormattedMessage
                defaultMessage="Enter valid regular expression"
                id="validation.isValidRegexExpression"
            />
        );
    }
    return undefined;
};

export const isValidArgumentValue = value =>
    !value ||
    !value.length ||
    (value.length > 0 && value.match(/^[0-9A-Za-z@./_-]+$/)) ? undefined : (
        <FormattedMessage
            defaultMessage="Only A-Z a-z 0-9 @ . / - _ allowed"
            id="validation.isValidArgumentValue"
        />
    );

export const isFieldSet = fieldName => (value, values) => {
    const { [fieldName]: field } = values;
    return !value && field ? (
        <FormattedMessage defaultMessage="Required" id="validation.isFieldSet" />
    ) : undefined;
};

export const isAtleastZero = isAtleast(0);

export const isGreaterThanZero = isGreaterThan(0);

export const isLessThanMaxInt = isLessThan(2147483647);

export const isLatitude = value =>
    !value ||
    (value &&
        String(value).match(
            /^([+-])?(?:90(?:(?:.0{1,6})?)|(?:[0-9]|[1-8][0-9])(?:(?:.[0-9]{1,6})?))$/
        )) ? undefined : (
        <FormattedMessage defaultMessage="Invalid latitude format" id="validation.isLatitude" />
    );

export const isLongitude = value =>
    !value ||
    (value &&
        String(value).match(
            /^([+-])?(?:180(?:(?:\.0{1,6})?)|(?:[0-9]|[1-9][0-9]|1[0-7][0-9])(?:(?:\.[0-9]{1,6})?))$/
        )) ? undefined : (
        <FormattedMessage defaultMessage="Invalid longitude format" id="validation.isLongitude" />
    );

export const isIp = value =>
    !value || ipRegex({ exact: true }).test(String(value)) ? undefined : (
        <FormattedMessage defaultMessage="Invalid IP" id="validation.isIp" />
    );

export const isDomain = value => {
    if (!value) {
        return undefined;
    }

    try {
        const urlHostname = new URL(`http://${value}`).hostname;
        return urlHostname === value ? undefined : (
            <FormattedMessage
                defaultMessage="Invalid FQDN (try {urlHostname})"
                id="validation.invalidHostnameFQDN"
                values={{
                    urlHostname,
                }}
            />
        );
    } catch {
        return <FormattedMessage defaultMessage="Invalid FQDN" id="validation.invalidFQDN" />;
    }
};

export const isURL = value => {
    if (!value) {
        return undefined;
    }

    try {
        new URL(value);
        return undefined;
    } catch {
        return <FormattedMessage defaultMessage="Invalid URL" id="validation.invalidURL" />;
    }
};

export const isAnyOf =
    (...validators) =>
    value => {
        const messages = validators.map(validator => validator(value));

        if (messages.every(val => val !== undefined)) {
            // NB! message can be FormattedMessage
            return messages.map((message, i) => (i === 0 ? message : <>, {message}</>));
        }

        return undefined;
    };

export const isIpOrDomain = isAnyOf(isIp, isDomain);

export const isValidEmail = value =>
    value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value) ? (
        <FormattedMessage
            defaultMessage="Enter a valid e-mail address"
            id="validation.isValidEmail"
        />
    ) : undefined;

export const isValidPhone = value =>
    value && !/^\+?[0-9]+$/i.test(value) ? (
        <FormattedMessage
            defaultMessage="Enter a valid phone number"
            id="validation.isValidPhone"
        />
    ) : undefined;

export const isValidPhoneAllowSpaces = value =>
    value && !/^\+?[0-9 ]+$/i.test(value) ? (
        <FormattedMessage
            defaultMessage="Enter a valid phone number"
            id="validation.isValidPhone"
        />
    ) : undefined;

export const isValidBTAddress = value =>
    value && !/^([0-9A-F]{2}[:]){5}([0-9A-F]{2})$/.test(value) ? (
        <FormattedMessage
            defaultMessage="Enter valid bluetooth address"
            id="validation.isValidBTAddress"
        />
    ) : undefined;

export const isValidDeviceSerial = value =>
    value && /-(gw|pc|mc)$/.test(value) ? (
        <FormattedMessage
            defaultMessage="Device serial cannot end with -gw/pc/mc"
            id="validation.isValidDeviceSerial"
        />
    ) : undefined;

export const isValidDurationString = value => {
    if (!value) return undefined;
    try {
        toSecondsFromDurationString(value);
        return undefined;
    } catch (err) {
        return err.message;
    }
};

export const isDurationStringInRangeSeconds = (min, max) => value => {
    if (!value) return undefined;
    try {
        const seconds = toSecondsFromDurationString(value);
        if (seconds < min || seconds > max) {
            return (
                <FormattedMessage
                    defaultMessage="Enter a value between {min} and {max}"
                    id="validation.isDurationStringInRange"
                    values={{
                        max: toDurationStringFromSeconds(max),
                        min: toDurationStringFromSeconds(min),
                    }}
                />
            );
        }
        return undefined;
    } catch (err) {
        return err.message;
    }
};

export const all =
    (...validators) =>
    (...validatorProps) =>
        validators.reduce((error, validator) => error || validator(...validatorProps), undefined);
